/**
 * Libreria schede (V63): elenco di società, nel contesto della squadra aperta.
 *
 * Le schede base stanno in una card per tipo, prima gli esercizi e poi le
 * partite, dieci righe per pagina, con la ricerca per nome sopra. Admin
 * legge senza «Nuova»; Mister e Dirigente scrivono, con la nuova in un
 * modale. Le varianti stanno sul dettaglio della base.
 */

import { useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ETICHETTA_TIPO_SCHEDA, TIPI_SCHEDA, type TipoScheda, puo } from '@calcio/shared';
import { SearchIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { PulsanteNuovo } from '../comuni/Azioni.js';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { usePaginazione } from '../comuni/Paginazione.js';
import { useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { DialogScheda } from './DialogScheda.js';
import { ICONA_TIPO_SCHEDA } from './icone.js';
import { RigaScheda } from './RigaScheda.js';
import type { SchedaInElenco } from './schede.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

/** La chiave della pagina di ogni gruppo nell'URL: una ricerca nuova le azzera. */
const pagina = (t: TipoScheda) => `pagina-${t}`;

export function ElencoSchede() {
  const { ss } = useParams<{ ss: string }>();
  return <Elenco key={ss} ss={ss!} />;
}

function Elenco({ ss }: { ss: string }) {
  const { utente } = useContesto();
  const { dati, errore } = useDati<SchedaInElenco[]>('/schede');
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState('');
  const gestisce = puo(utente.assegnazioni, 'allenamento.gestisci', ss);

  const basi = useMemo(() => (dati ?? []).filter((s) => !s.schedaBaseId), [dati]);
  const visibili = useMemo(() => {
    const testo = q.trim().toLocaleLowerCase('it');
    return basi.filter((s) => !testo || s.nome.toLocaleLowerCase('it').includes(testo));
  }, [basi, q]);

  const cerca = (testo: string) => {
    setQ(testo);
    const p = new URLSearchParams(params);
    for (const t of TIPI_SCHEDA) p.delete(pagina(t));
    setParams(p, { replace: true });
  };

  const [nuovaAperta, setNuovaAperta] = useState(false);

  const nuova = gestisce && (
    <PulsanteNuovo etichetta="Nuova scheda" onClick={() => setNuovaAperta(true)} />
  );
  const modale = gestisce && (
    <DialogScheda ss={ss} aperto={nuovaAperta} onChiudi={() => setNuovaAperta(false)} />
  );

  if (!dati) {
    return (
      <>
        <Testata titolo="Libreria schede" voce="schede" />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  return (
    <>
      <Testata titolo="Libreria schede" voce="schede" azione={basi.length > 0 && nuova} />
      {modale}
      <Corpo>
        {basi.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyTitle>Nessuna scheda</EmptyTitle>
              <EmptyDescription>
                {gestisce
                  ? 'Una scheda è un esercizio con mix a somma 100, RPE e minuti di riferimento. Le varianti si creano dalla scheda base.'
                  : 'Mister e Dirigente non hanno ancora creato schede.'}
              </EmptyDescription>
            </EmptyHeader>
            {nuova && <EmptyContent>{nuova}</EmptyContent>}
          </Empty>
        ) : (
          <div className="flex flex-col gap-4">
            <InputGroup className="@xl:max-w-sm">
              <InputGroupInput
                placeholder="Cerca per nome"
                aria-label="Cerca per nome"
                value={q}
                onChange={(e) => cerca(e.target.value)}
              />
              <InputGroupAddon>
                <SearchIcon />
              </InputGroupAddon>
            </InputGroup>
            {visibili.length === 0 && <StatoVuoto titolo="Nessuna scheda con questo nome" />}
            {TIPI_SCHEDA.map((t) => (
              <Gruppo key={t} tipo={t} schede={visibili} ss={ss} />
            ))}
          </div>
        )}
      </Corpo>
    </>
  );
}

function Gruppo({ tipo, schede, ss }: { tipo: TipoScheda; schede: SchedaInElenco[]; ss: string }) {
  const delGruppo = schede.filter((s) => s.tipo === tipo);
  const { righe, barra, inizio } = usePaginazione(delGruppo, pagina(tipo));
  if (delGruppo.length === 0) return null;
  const Icona = ICONA_TIPO_SCHEDA[tipo];
  return (
    // Sotto la testata fissa del telefono, tornandoci cambiando pagina.
    <Card ref={inizio} className="scroll-mt-(--altezza-testata) bg-muted">
      <CardHeader>
        <CardTitle className="flex items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <Icona aria-hidden className="size-5" />
          <h2>{ETICHETTA_TIPO_SCHEDA[tipo]}</h2>
          <Badge className="tabular-nums">{delGruppo.length}</Badge>
        </CardTitle>
      </CardHeader>
      {/* Righe su due colonne quando la card ne ha lo spazio; ogni riga si
          dispone sulla sua larghezza. */}
      <CardContent className="flex flex-col gap-4 px-2 @container @xl:px-(--card-spacing)">
        <ul className="grid gap-2 @3xl:grid-cols-2">
          {righe.map((s) => (
            <li key={s.id} className="@container">
              <RigaScheda scheda={s} ss={ss} />
            </li>
          ))}
        </ul>
        {barra}
      </CardContent>
    </Card>
  );
}
